import React, { useState, useEffect } from 'react';
import {
  Calendar, CheckCircle, XCircle, Clock, Eye, Search, RefreshCw,
  Users, Building2, Globe, MapPin, Megaphone, CreditCard, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminEventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending_approval');
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [pricing, setPricing] = useState({ price: 49999, features: [] });
  const [showPricing, setShowPricing] = useState(false);

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchEvents(); fetchPricing(); }, [statusFilter]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/api/admin/events${params}`, { headers });
      setEvents(res.data.events || []);
    } catch (err) {
      toast.error('Failed to load events');
    } finally { setLoading(false); }
  };

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/event-promotion-pricing`, { headers });
      setPricing(res.data);
    } catch { }
  };

  const handleAction = async (eventId, action) => {
    try {
      await axios.put(`${API}/api/admin/events/${eventId}/action`, { action, reason: actionReason }, { headers });
      toast.success(`Event ${action}d`);
      setActionReason('');
      setExpandedEvent(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleUpdatePricing = async () => {
    try {
      await axios.put(`${API}/api/admin/event-promotion-pricing`, pricing, { headers });
      toast.success('Pricing updated');
    } catch { toast.error('Failed to update pricing'); }
  };

  const handlePromotionStatus = async (eventId, status) => {
    try {
      await axios.put(`${API}/api/admin/events/${eventId}/promotion?status=${status}`, {}, { headers });
      toast.success(`Promotion ${status}`);
      fetchEvents();
    } catch { toast.error('Failed'); }
  };

  const stats = {
    total: events.length,
    pending: events.filter(e => e.status === 'pending_approval').length,
    approved: events.filter(e => e.status === 'approved').length,
    rejected: events.filter(e => e.status === 'rejected').length,
  };

  return (
    <div className="space-y-6" data-testid="admin-events-section">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Events', value: stats.total, color: 'border-blue-500', text: 'text-blue-600' },
          { label: 'Pending', value: stats.pending, color: 'border-amber-500', text: 'text-amber-600' },
          { label: 'Approved', value: stats.approved, color: 'border-emerald-500', text: 'text-emerald-600' },
          { label: 'Rejected', value: stats.rejected, color: 'border-red-500', text: 'text-red-600' },
        ].map((s, i) => (
          <div key={i} className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${s.color}`}>
            <p className="text-gray-500 text-sm">{s.label}</p>
            <p className={`text-2xl font-bold ${s.text}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="admin-event-status-filter">
            <option value="">All Events</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowPricing(!showPricing)} className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100 flex items-center gap-2" data-testid="toggle-pricing-btn">
            <CreditCard size={16} /> Promotion Pricing
          </button>
          <button onClick={fetchEvents} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* Promotion Pricing Panel */}
      {showPricing && (
        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-purple-200" data-testid="promotion-pricing-panel">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Megaphone size={18} /> Event Promotion Pricing</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (INR)</label>
              <input type="number" value={pricing.price} onChange={e => setPricing({ ...pricing, price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="promotion-price-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Features (one per line)</label>
              <textarea value={pricing.features?.join('\n')} onChange={e => setPricing({ ...pricing, features: e.target.value.split('\n').filter(Boolean) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-24 resize-none" />
            </div>
          </div>
          <button onClick={handleUpdatePricing} className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700" data-testid="save-pricing-btn">
            Save Pricing
          </button>
        </div>
      )}

      {/* Events List */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">No events found</div>
      ) : (
        <div className="space-y-3">
          {events.map(event => (
            <div key={event.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`admin-event-${event.id}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="font-semibold text-gray-800 text-base">{event.title}</h4>
                      <StatusBadge status={event.status} />
                      {event.is_paid && <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-medium">Paid ₹{event.price}</span>}
                      {event.promotion_status !== 'none' && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${event.promotion_status === 'active' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                          Promo: {event.promotion_status}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1"><Building2 size={12} />{event.company_name}</span>
                      <span className="flex items-center gap-1"><Calendar size={12} />{event.event_date || 'No date'}</span>
                      <span className="flex items-center gap-1">{event.event_type === 'offline' ? <MapPin size={12} /> : <Globe size={12} />}{event.event_type}</span>
                      <span className="flex items-center gap-1"><Users size={12} />{event.registration_count || 0} registered</span>
                      <span className="flex items-center gap-1">{event.visibility === 'public' ? <Eye size={12} /> : 'Private'}</span>
                    </div>
                    {event.description && <p className="text-sm text-gray-500 mt-2 line-clamp-2">{event.description}</p>}
                  </div>
                  <button onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                    {expandedEvent === event.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
              </div>

              {/* Expanded Actions */}
              {expandedEvent === event.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3">
                  {event.status === 'pending_approval' && (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input value={actionReason} onChange={e => setActionReason(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Reason (optional)" />
                      <button onClick={() => handleAction(event.id, 'approve')} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700" data-testid={`approve-event-${event.id}`}>
                        Approve
                      </button>
                      <button onClick={() => handleAction(event.id, 'reject')} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700" data-testid={`reject-event-${event.id}`}>
                        Reject
                      </button>
                    </div>
                  )}
                  {event.promotion_status === 'pending' && (
                    <div className="flex gap-3">
                      <button onClick={() => handlePromotionStatus(event.id, 'active')} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">Activate Promotion</button>
                      <button onClick={() => handlePromotionStatus(event.id, 'none')} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300">Cancel Promotion</button>
                    </div>
                  )}
                  {event.categories?.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap">
                      {event.categories.map((c, i) => <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{c}</span>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending_approval: { label: 'Pending', color: 'bg-amber-50 text-amber-700' },
    approved: { label: 'Approved', color: 'bg-emerald-50 text-emerald-700' },
    rejected: { label: 'Rejected', color: 'bg-red-50 text-red-700' },
  };
  const s = map[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.color}`}>{s.label}</span>;
};

export default AdminEventsSection;
